// RAM / OS requirement check from the environment snapshot (./environment.js).
// Pure — unit-tested in __tests__/requirements.test.js.
import { DEVICE_CATEGORY_RULES } from './thresholds';

export const REQUIREMENT_STATUS = Object.freeze({
  PASS: 'pass',
  FAIL: 'fail',
  // The browser does not tell us — shown, never judged.
  UNKNOWN: 'unknown',
  // No rule for this platform (Linux, ChromeOS).
  NOT_REQUIRED: 'not-required',
});

// Chromium's reduced UA freezes every Android version to "Android 10; K". The
// real one only comes from Client Hints, which need a secure context (HTTPS or
// localhost) — over plain-http LAN testing the frozen value is all there is.
const FROZEN_ANDROID_UA = /Android 10; K\)/;

// "Windows NT x.y" → marketing version. NT 10.0 is sent by both 10 and 11.
const WINDOWS_NT_VERSIONS = { '10.0': 10, 6.3: 8.1, 6.2: 8, 6.1: 7, '6.0': 6 };

// What parseUserAgent calls an iPad whose Safari requests the desktop site.
const IPAD_AS_MAC = 'iPadOS (reports as macOS)';

const majorOf = version => {
  const major = Number.parseInt(String(version ?? ''), 10);
  return Number.isFinite(major) ? major : null;
};

const versionStatus = (major, minimum) =>
  major === null
    ? REQUIREMENT_STATUS.UNKNOWN
    : major >= minimum
      ? REQUIREMENT_STATUS.PASS
      : REQUIREMENT_STATUS.FAIL;

function checkRam(environment, rules) {
  const valueGb = environment?.deviceMemoryGb ?? null;
  return {
    valueGb,
    requiredGb: rules.minRamGb,
    status:
      valueGb === null
        ? REQUIREMENT_STATUS.UNKNOWN
        : valueGb >= rules.minRamGb
          ? REQUIREMENT_STATUS.PASS
          : REQUIREMENT_STATUS.FAIL,
  };
}

function resolveWindowsVersion(environment) {
  const fromHints = environment?.osVersionReportedByClientHints;
  if (fromHints) {
    // resolveOsVersion maps Client Hints to '11' / '10' / '7/8/8.1'.
    return { version: fromHints, number: fromHints === '7/8/8.1' ? 8 : majorOf(fromHints) };
  }
  const nt = String(environment?.osVersionReportedByUA ?? environment?.osVersion ?? '').replace(
    /^NT\s*/,
    ''
  );
  const number = WINDOWS_NT_VERSIONS[nt] ?? null;
  if (number === null) {
    return { version: null, number: null };
  }
  return { version: number === 10 ? '10 or 11' : String(number), number };
}

// Every macOS 11+ browser freezes its UA at "Mac OS X 10_15_7"; only Client
// Hints (Chromium, secure context) carry the real version. A UA below 10.15 is
// a genuinely old Mac.
function resolveMacVersion(environment) {
  if (environment?.osVersionSource === 'client-hints') {
    const version = environment.osVersion ?? null;
    return { version, major: majorOf(version) };
  }
  const [major, minor] = String(environment?.osVersion ?? '')
    .split('.')
    .map(part => Number.parseInt(part, 10));
  if (major === 10 && Number.isFinite(minor) && minor < 15) {
    return { version: environment.osVersion, major: 10 };
  }
  return { version: null, major: null };
}

function checkOs(environment, rules) {
  const name = environment?.os ?? null;

  if (name === 'Android') {
    const frozen =
      environment?.osVersionSource !== 'client-hints' &&
      FROZEN_ANDROID_UA.test(environment?.userAgent ?? '');
    const version = frozen ? null : (environment?.osVersion ?? null);
    const major = majorOf(version);
    return {
      name,
      version,
      required: `Android ${rules.minAndroidVersion}+`,
      status:
        major === null
          ? REQUIREMENT_STATUS.UNKNOWN
          : major >= rules.minAndroidVersion
            ? REQUIREMENT_STATUS.PASS
            : REQUIREMENT_STATUS.FAIL,
      note: frozen
        ? 'The browser hides the real Android version here (open the page over HTTPS to read it).'
        : null,
    };
  }

  if (name === 'Windows') {
    const { version, number } = resolveWindowsVersion(environment);
    return {
      name,
      version,
      required: `Windows ${rules.minWindowsVersion}+ on ${rules.requiredWindowsCpu}`,
      status:
        number === null
          ? REQUIREMENT_STATUS.UNKNOWN
          : number >= rules.minWindowsVersion
            ? REQUIREMENT_STATUS.PASS
            : REQUIREMENT_STATUS.FAIL,
      note: `The browser does not reveal the processor — check it is ${rules.requiredWindowsCpu}.`,
    };
  }

  if (name === 'iOS') {
    const version = environment?.osVersion ?? null;
    return {
      name,
      version,
      required: `iOS ${rules.minIosVersion}+ on iPhone ${rules.minIphoneModel} or newer`,
      status: versionStatus(majorOf(version), rules.minIosVersion),
      note: `The browser does not reveal the iPhone model — check it is an iPhone ${rules.minIphoneModel} or newer.`,
    };
  }

  if (name === 'iPadOS' || name === IPAD_AS_MAC) {
    // Desktop-mode Safari hides the iPadOS version behind a frozen macOS one.
    const version = name === 'iPadOS' ? (environment?.osVersion ?? null) : null;
    return {
      name,
      version,
      required: `iPadOS ${rules.minIpadOsVersion}+ on an iPad with ${rules.requiredIpadChips}`,
      status: versionStatus(majorOf(version), rules.minIpadOsVersion),
      note:
        name === IPAD_AS_MAC
          ? `Safari hides the iPadOS version in desktop mode. The browser does not reveal the iPad chip — check it has ${rules.requiredIpadChips}.`
          : `The browser does not reveal the iPad chip — check it has ${rules.requiredIpadChips}.`,
    };
  }

  if (name === 'macOS') {
    const { version, major } = resolveMacVersion(environment);
    return {
      name,
      version,
      required: `macOS ${rules.minMacOsVersion}+ on ${rules.requiredMacChips}`,
      status: versionStatus(major, rules.minMacOsVersion),
      note:
        version === null
          ? `The browser hides the real macOS version (open the page in Chrome over HTTPS to read it). It does not reveal the processor either — check it is ${rules.requiredMacChips}.`
          : `The browser does not reveal the processor — check it is ${rules.requiredMacChips}.`,
    };
  }

  return {
    name,
    version: environment?.osVersion ?? null,
    required: null,
    status: REQUIREMENT_STATUS.NOT_REQUIRED,
    note: null,
  };
}

/**
 * @param {object} environment from collectEnvironment()
 * @returns {{ ram: object, os: object, passes: boolean }} `passes` is false only
 *   when a requirement is known to fail — unknown never fails a device.
 */
export function checkRequirements(environment, rules = DEVICE_CATEGORY_RULES) {
  const ram = checkRam(environment, rules);
  const os = checkOs(environment, rules);
  return {
    ram,
    os,
    passes: ram.status !== REQUIREMENT_STATUS.FAIL && os.status !== REQUIREMENT_STATUS.FAIL,
  };
}
